import type { FunnelStage } from '@/api/types'
import { KpiCard } from '@/design'

import { FUNNEL_KPI_KEYS, FUNNEL_KPIS, readFunnelKpis, type FunnelKpiReading } from './definitions'
import { formatCount, formatRate, NOT_MEASURED } from './figures'

function formatted(reading: FunnelKpiReading): string {
  if (!reading.measured) return NOT_MEASURED
  return reading.definition.format === 'percent' ? formatRate(reading.value) : formatCount(reading.value)
}

/** Why a tile shows no change: the reason it has no figure, or what the previous period lacked. */
function note(reading: FunnelKpiReading, comparing: boolean): string | undefined {
  if (reading.reason) return reading.reason
  if (!comparing) return undefined
  if (reading.previousReason) return reading.previousReason
  if (reading.previous === null) return 'No figure for the previous period'
  if (reading.previous === 0) return 'None in the previous period'
  return undefined
}

/** The six headline figures, each against the previous period, coloured by whether the move is good news. */
export function FunnelKpis({ stages, compareLabel }: { stages: readonly FunnelStage[]; compareLabel: string | null }) {
  const comparing = compareLabel !== null
  return (
    <section aria-label="Funnel headline figures">
      <ul className="fun-kpis">
        {readFunnelKpis(stages).map((reading) => (
          <li key={reading.definition.key} data-kpi={reading.definition.key} data-measured={reading.measured}>
            <KpiCard
              label={reading.definition.label}
              value={formatted(reading)}
              change={reading.change}
              polarity={reading.definition.polarity}
              comparisonLabel={compareLabel}
              note={note(reading, comparing)}
              detail={reading.caveat ?? reading.definition.summary}
            />
          </li>
        ))}
      </ul>
    </section>
  )
}

/** Each headline's full definition, folded away beneath the row. */
export function FunnelDefinitions() {
  return (
    <details className="fun-definitions">
      <summary>How these figures are measured</summary>
      <dl>
        {FUNNEL_KPI_KEYS.map((key) => (
          <div key={key}>
            <dt>{FUNNEL_KPIS[key].label}</dt>
            <dd>{FUNNEL_KPIS[key].definition}</dd>
          </div>
        ))}
      </dl>
      <p>
        A change is relative to the same figure in the comparison period: a rate moving from 10% to 12% is +20%. A
        figure reads “Not measured” when a stage it uses had no events in the period, and “—” when there was nothing to
        divide by.
      </p>
    </details>
  )
}
